import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BillingTrial, BillingView, CardSummary, Invoice, PercentOff, cancelPlan, createPortalSession, deleteFormUploads,
  getBilling, getDowngradeCheck, getStorage, invoicePdfHref, setBillingEmail,
} from '../../api/admin';
import { ApiRequestError } from '../../api/client';
import { DowngradeCheck, PlanLimits, StorageForm } from '../../shared/types';
import {
  INFINITY, formatStorage, longDate, money, moneyExact,
} from '../platform/planDisplay';
import { FigureTile, RowCard, TableCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * Settings → Billing & plan (R11, screen 33): the plan, this month's usage
 * against its ceilings, the card, the invoices, and per-form storage.
 *
 * Owners only — the page is absent for an admin and the server refuses the
 * routes regardless. Cards and receipts live in Stripe; this page links out
 * for them and never renders a field a card number could be typed into. The
 * card line shows the brand, the last four digits and the expiry Stripe
 * reports, nothing more.
 *
 * The design's four tiles lead; every other meter the plan has sits under
 * "Show all usage" — and any meter at or near its ceiling is shown with the
 * four whatever the switch says, so a problem is never folded away.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** "18 Sep 2026" — the invoice table's date. */
const shortDMY = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

/** "18 Sep" — the trial pill's date. */
const shortDM = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
};

const BRAND: Record<string, string> = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover',
  diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay', cartes_bancaires: 'Cartes Bancaires',
  eftpos_au: 'eftpos', interac: 'Interac', link: 'Link',
};

/** "Visa •• 4242 · expires 12/28" — the design's card line, from what Stripe reported. */
export function cardLine(card: CardSummary): string {
  const brand = BRAND[card.brand] || (card.brand ? card.brand[0].toUpperCase() + card.brand.slice(1) : 'Card');
  const exp = card.expMonth && card.expYear
    ? ` · expires ${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
    : '';
  return `${brand} •• ${card.last4}${exp}`;
}

/**
 * What a free period turns into when it ends (D3), said in one sentence:
 * a period that converts keeps the plan and charges the card; one that does
 * not goes back to the plan the grant recorded, named with its price — or,
 * when Stripe holds a subscription for it, the subscription ends with the
 * period and the account moves to Free, which is what the webhook does.
 */
export function freePeriodSentence(trial: BillingTrial): string {
  const until = `Free ${trial.planName} until ${shortDM(trial.expiresAt)}`;
  const per = (i?: string) => (i === 'year' ? 'yr' : 'mo');
  if (trial.autoConvert) {
    const amount = trial.amountCents != null ? ` at ${money(trial.amountCents, trial.currency)}/${per(trial.interval)}` : '';
    return `${until}; then ${trial.planName}${amount}, charged to the card on file.`;
  }
  if (trial.endsSubscription) {
    // What the server does: the subscription was set to end with the period,
    // and its end moves the account to Free (stripeWebhook onSubscriptionDeleted).
    return `${until}; then the subscription ends and the account moves to Free. Nothing is charged for the free period.`;
  }
  const prev = trial.previousPlan;
  if (!prev) return `${until}; then back to your paid plan. Nothing is charged for the free period.`;
  const price = prev.cents ? ` at ${money(prev.cents, prev.currency)}/${per(prev.interval)}` : '';
  return `${until}; then back to ${prev.name}${price}. Nothing is charged for the free period.`;
}

/** "15% off every invoice until 3 October 2027" — or with no end, "15% off every invoice". */
export function percentOffLine(p: PercentOff): string {
  return `${p.percent}% off every invoice${p.endsAt ? ` until ${longDate(p.endsAt)}` : ''}`;
}

/**
 * Under the Agreed price and the Discount rows (Ben, 2026-09-26): any plan
 * change ends either one — the plan picker here, and the Stripe billing
 * portal too, where the webhook ends it (the portal shows only list prices).
 */
export const DISCOUNT_ENDS_HINT = 'Changing your plan — here or in the billing portal — ends it.';

/** The ?checkout= a Stripe return lands with (stripeService's billingUrl), in words. */
const RETURN_NOTE: Record<string, { tone: 'bl-ok' | 'bl-info' | 'bl-amber'; pill: string; text: string }> = {
  // Also where a paid signup's trial Checkout lands, so it claims no payment.
  success: {
    tone: 'bl-ok', pill: 'Back from Stripe',
    text: 'Checkout is finished at Stripe. Your plan updates here once Stripe confirms it — usually within a minute.',
  },
  // Not a Stripe return: the plan picker's in-place upgrade lands here.
  upgraded: {
    tone: 'bl-ok', pill: 'Upgraded',
    text: 'Stripe has the change. Your new plan shows here as soon as Stripe confirms it — usually within a minute.',
  },
  cancelled: {
    tone: 'bl-info', pill: 'Checkout closed',
    text: 'You left Stripe’s checkout before paying. Nothing was charged and your plan has not changed.',
  },
  'trial-started': {
    tone: 'bl-ok', pill: 'Card saved',
    text: 'Your card is saved with Stripe and your free period is set up.',
  },
  'trial-cancelled': {
    tone: 'bl-amber', pill: 'No card added',
    text: 'You left Stripe before adding a card. Nothing was charged.',
  },
};

export default function BillingSettings() {
  const [data, setData] = useState<BillingView | null>(null);
  const [error, setError] = useState('');
  const [portalNote, setPortalNote] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  // The gate against Free, fetched on load and re-checked on focus, so the
  // Cancel confirm is inert exactly while something still blocks.
  const [freeCheck, setFreeCheck] = useState<DowngradeCheck | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  // Where receipts go. Editable here because the AP contact is rarely whoever
  // signed up, and needing to ask support to change it is how it goes stale.
  const [emailDraft, setEmailDraft] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  /** Only ever a REFUSAL now; the confirmation is a toast. */
  const [emailError, setEmailError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  // A Stripe return (?checkout=…) is said once, then dropped from the address
  // so a reload does not say it again.
  const [returned, setReturned] = useState<string | null>(
    () => new URLSearchParams(location.search).get('checkout'),
  );
  useEffect(() => {
    if (!returned) return;
    const q = new URLSearchParams(location.search);
    q.delete('checkout');
    const rest = q.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The billing email is the one thing on this page typed rather than chosen,
  // so it is the one thing that can be lost by leaving.
  const { dirty, markSaved } = useDirty(emailDraft, !!data);
  const guard = useUnsavedGuard({
    dirty, busy: emailBusy,
    what: 'Your change to the billing email',
    onSave: () => onSaveEmail(),
    saveLabel: 'Save it and leave',
  });

  useEffect(() => {
    getBilling()
      .then((d) => { setData(d); setEmailDraft(d.subscription.billingEmail || ''); })
      .catch((e) => setError(e.message));
  }, []);

  // Back from a finished checkout, the plan lands when Stripe's webhook does:
  // read the page again a little later, twice, so it shows without a reload.
  // Keyed on what the page ARRIVED with, so dismissing the notice keeps it.
  const [arrivedWith] = useState(returned);
  useEffect(() => {
    if (arrivedWith !== 'success' && arrivedWith !== 'trial-started' && arrivedWith !== 'upgraded') return undefined;
    const reread = () => { getBilling().then(setData).catch(() => undefined); };
    const timers = [window.setTimeout(reread, 4000), window.setTimeout(reread, 12000)];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [arrivedWith]);

  const runFreeCheck = useCallback(() => {
    getDowngradeCheck('free').then((r) => setFreeCheck(r.check)).catch(() => undefined);
  }, []);

  useEffect(() => {
    runFreeCheck();
    window.addEventListener('focus', runFreeCheck);
    return () => window.removeEventListener('focus', runFreeCheck);
  }, [runFreeCheck]);

  async function openPortal() {
    setPortalBusy(true);
    setPortalNote('');
    try {
      const { url } = await createPortalSession();
      // Same tab, deliberately. The portal is a hosted page with its own
      // "return to Form-Magic" link pointing back here — opening it in a new
      // tab means that link lands the customer on a SECOND copy of the app.
      window.location.assign(url);
      return; // navigating away; leave the button disabled rather than flicker
    } catch (e) {
      setPortalNote(e instanceof ApiRequestError && e.status === 503
        ? "Stripe isn't connected on this install yet, so there is no portal to open."
        : (e as Error).message);
    }
    setPortalBusy(false);
  }

  async function onSaveEmail() {
    setEmailBusy(true);
    setEmailError('');
    try {
      const r = await setBillingEmail(emailDraft.trim());
      markSaved();
      toast(r.billingEmail
        ? `Saved — receipts and billing notices go to ${r.billingEmail}.`
        : 'Cleared — we have no address to send receipts or billing notices to.');
      getBilling().then(setData).catch(() => undefined);
    } catch (e) {
      setEmailError((e as Error).message);
      setEmailBusy(false);
      throw e;   // so "Save it and leave" stays put rather than losing the address
    }
    setEmailBusy(false);
  }

  async function onCancel() {
    if (!confirmCancel) { setConfirmCancel(true); return; }
    try {
      const r = await cancelPlan();
      if (!r.ok) { setFreeCheck(r.check); return; }
      setConfirmCancel(false);
      setCancelNote(r.scheduled && r.effectiveAt
        ? `Scheduled — you move to Free on ${longDate(r.effectiveAt)}. Everything you have stays.`
        : 'Done — you are on Free. Everything you have stays.');
      getBilling().then(setData).catch(() => undefined);
    } catch (e) { setError((e as Error).message); }
  }

  if (error) return <div className="ad-err">{error}</div>;
  if (!data) return <div className="fs-card pad st-col">Loading…</div>;

  const { plan, price, plans, usage, invoices, billingConnected, account, subscription, entitlement, trial } = data;
  const ent = entitlement;
  const interval = subscription.billingInterval;
  // What converting charges: the server says (R11); an older answer without
  // it is priced from the catalogue at the account's interval, as before.
  const trialPlan = trial ? plans.find((p) => p.key === trial.planKey) ?? null : null;
  const trialAmountCents = !trial ? null
    : trial.amountCents !== undefined ? trial.amountCents
      : trialPlan ? (interval === 'year' ? trialPlan.price_annual_cents : trialPlan.price_monthly_cents) : null;
  const per = interval === 'year' ? 'yr' : 'mo';
  const pastDue = subscription.status === 'past_due';
  const returnNote = returned ? RETURN_NOTE[returned] : undefined;

  // The line under the title: what you are on and what happens next.
  const headline = trial
    ? `${ent.plan.name} · free ${trial.planName} until ${shortDM(trial.expiresAt)}`
    : subscription.currentPeriodEnd && price.cents
      ? `${ent.plan.name} · renews ${shortDM(subscription.currentPeriodEnd)}`
      : ent.plan.name;

  return (
    <div className="st-stack bp-page">
      <div className="fs-page-head">
        <div className="text">
          <h1>Billing &amp; plan</h1>
          <div className="fs-page-sub">{headline}</div>
        </div>
        <div className="bp-head-actions">
          <button className="fs-btn neutral" disabled={portalBusy} onClick={openPortal}>
            Payment method &amp; receipts
          </button>
          <Link className="fs-btn primary" to="/admin/settings/billing/plans">Change plan</Link>
        </div>
      </div>

      {returnNote && (
        <div className={`bl-banner ${returnNote.tone}`} role="status">
          <span className="bl-banner-pill">{returnNote.pill}</span>
          <span className="bl-banner-text">{returnNote.text}</span>
          <button className="fs-btn sm neutral" onClick={() => setReturned(null)}>Dismiss</button>
        </div>
      )}
      {portalNote && <div className="bl-banner bl-amber" role="status"><span className="bl-banner-text">{portalNote}</span></div>}

      {data.readOnlyForms && data.readOnlyForms.length > 0 && (
        <div className="bl-banner bl-amber">
          <span className="bl-banner-pill">Read-only</span>
          <span className="bl-banner-text">
            <b>{data.readOnlyForms.length} form{data.readOnlyForms.length === 1 ? ' is' : 's are'} read-only</b>{' '}
            — above your current plan&apos;s limits after an involuntary change, kept whole but not
            collecting: {data.readOnlyForms.map((f) => f.name).join(', ')}.
          </span>
          <Link className="bl-banner-link" to="/admin">Open the forms list</Link>
        </div>
      )}

      {/* The change already made, said before the invoice says it. Nothing
          local records a pending move — this is read from Stripe on load — so
          without this line "we scheduled it" and "it never reached Stripe"
          look exactly alike right up until the renewal bills. */}
      {data.scheduledChange && (
        <div className="bl-banner bl-info">
          <span className="bl-banner-pill">Scheduled</span>
          <span className="bl-banner-text">
            {data.scheduledChange.unknownPlan ? (
              <>
                <b>A plan change is scheduled at Stripe</b> — but it points at a price that matches no
                plan here, so we cannot name it. Please get in touch before your renewal.
              </>
            ) : (
              <>
                <b>Scheduled: you move to {data.scheduledChange.planName}
                  {data.scheduledChange.effectiveAt ? ` on ${shortDM(data.scheduledChange.effectiveAt)}` : ' at your next renewal'}.</b>{' '}
                Nothing is charged before then, and you stay on {ent.plan.name} until it happens
                {data.scheduledChange.amountCents !== null && (
                  <>, when billing becomes {money(data.scheduledChange.amountCents, data.scheduledChange.currency)}
                    {data.scheduledChange.interval === 'year' ? ' a year' : ' a month'}</>
                )}.
              </>
            )}
          </span>
        </div>
      )}

      {/* ---- This month ---- */}
      <UsageSection usage={usage} limits={ent.limits} />

      {/* ---- Your plan ---- */}
      <section className="fs-card st-card bp-yourplan" aria-labelledby="bp-plan-title">
        <div className="st-card-head"><h2 id="bp-plan-title">Your plan</h2></div>

        <RowCard
          label="Plan"
          actions={<Link className="fs-btn text" to="/admin/settings/billing/plans">Change</Link>}
        >
          <div className="bp-plan-line">
            <b className="bp-current-name">{ent.plan.name}</b>
            {data.scheduledChange && !data.scheduledChange.unknownPlan && (
              <span className="fs-pill info">→ {data.scheduledChange.planName} at renewal</span>
            )}
            {trial && <span className="fs-pill identity">free until {shortDM(trial.expiresAt)}</span>}
            {!!account.creditDays && !trial && <span className="fs-pill ok">{account.creditDays}-day credit</span>}
            {pastDue && <span className="fs-pill danger">Past due</span>}
            {account.status !== 'active' && <span className="fs-pill neutral">{account.status}</span>}
          </div>
          <div className="bp-restated">
            {trial && !trial.autoConvert
              // A period that does not convert charges nothing after it, so the
              // line says what it is rather than a price that will never bill.
              ? `Free until ${longDate(trial.expiresAt)} · ${limitsDigest(ent.limits, ent.features.sso)}`
              : planRestated(trial ? trialAmountCents : price.cents, interval, !!trial, ent.limits, ent.features.sso)}
          </div>
        </RowCard>

        {price.overridden && (
          <RowCard label="Agreed price" hint={DISCOUNT_ENDS_HINT}>
            <b>An agreed price.</b> Your rate is {money(price.cents, price.currency)}
            {price.listCents !== null && <> rather than the {money(price.listCents, price.currency)} list price</>}
            {price.interval === 'year' ? ' a year' : ' a month'}.
            {price.note && <> {price.note}</>}
          </RowCard>
        )}

        {/* A running percent-off (D4), beside where an agreed price would be —
            one discount at a time (Ben, 2026-09-26), so never both. */}
        {data.percentOff && (
          <RowCard label="Discount" hint={DISCOUNT_ENDS_HINT}>{percentOffLine(data.percentOff)}</RowCard>
        )}

        {trial ? (
          <>
            {/* What the free period turns into when it ends (D3). */}
            <RowCard label="Free period">{freePeriodSentence(trial)}</RowCard>
            <RowCard label="First charge">
              {trial.autoConvert && trial.expiresAt ? longDate(trial.expiresAt) : '—'}
            </RowCard>
            <RowCard label="Amount">
              {trial.autoConvert ? moneyExact(trialAmountCents) : '$0 — not converting'}
            </RowCard>
            <RowCard label="Paid plan underneath">
              {`${plan.name}${price.cents ? ` · ${money(price.cents)}/${per}` : ''}`}
            </RowCard>
          </>
        ) : subscription.currentPeriodEnd ? (
          <RowCard label="Next invoice" hint={interval === 'year' ? 'billed annually, up front' : 'billed monthly'}>
            {longDate(subscription.currentPeriodEnd)} · {moneyExact(price.cents)}
          </RowCard>
        ) : null}

        {/* A credit is money off ONE invoice, so it is stated as such (D5) —
            "you are in a free period" over-promised the moment the credit
            ran out, and that is the sentence a customer quotes back. */}
        {!!account.creditDays && (
          <RowCard label="Credit">
            <b>{account.creditDays} days credit</b> on your next invoice
            {account.creditReason ? ` — ${account.creditReason}` : ''}. It comes off automatically, once.
          </RowCard>
        )}

        {pastDue && (
          <RowCard label="Payment">
            <span className="bp-danger-text">The last payment failed.</span>{' '}
            {subscription.graceUntil
              ? <>Your plan stays whole until {longDate(subscription.graceUntil)} while Stripe retries.</>
              : <>Stripe is retrying; update the card to settle it.</>}
          </RowCard>
        )}

        <CardRow
          billingConnected={billingConnected}
          hasPaymentMethod={subscription.hasPaymentMethod}
          card={data.paymentMethod ?? null}
          portalBusy={portalBusy}
          onPortal={openPortal}
        />

        {/* Receipts, plan-change notices and retention warnings all go here
            whether or not a card exists, and an account is likeliest to need
            this set BEFORE its first upgrade. */}
        <RowCard
          label={<label htmlFor="bl-email">Billing email</label>}
          hint={subscription.billingEmail
            ? 'Receipts, plan changes and renewal notices go here.'
            : 'Not set — we have nowhere to send receipts or renewal notices.'}
          actions={(
            <button className="fs-btn neutral sm"
              disabled={emailBusy || emailDraft.trim() === (subscription.billingEmail || '')}
              onClick={() => { void onSaveEmail().catch(() => undefined); }}>
              {emailBusy ? 'Saving…' : 'Save billing email'}
            </button>
          )}
        >
          <input id="bl-email" type="email" className="fs-input bp-email" value={emailDraft}
            placeholder="ap@yourcompany.com" onChange={(e) => setEmailDraft(e.target.value)} />
          {emailError && <div className="ad-err bp-row-err">{emailError}</div>}
        </RowCard>

        {plan.key !== 'free' && (
          <RowCard
            label="Cancel"
            hint="Everything you have stays."
            actions={(
              <button className="fs-btn text danger" onClick={onCancel}
                disabled={confirmCancel && (!freeCheck || freeCheck.blocked)}>
                {confirmCancel ? 'Confirm — move to Free' : 'Cancel — move to Free'}
              </button>
            )}
          >
            {subscription.hasSubscription
              ? 'Move to Free at the end of your billing period.'
              : 'Move to Free now — there is no subscription to run out.'}
            {confirmCancel && freeCheck && (
              <div className="bp-impact">
                <b>Before this goes through.</b>{' '}
                {freeCheck.blocked
                  ? 'Free cannot hold what you have yet — clear these first:'
                  : subscription.hasSubscription
                    ? 'Everything fits under the Free limits. Confirming schedules the move at the end of your billing period; nothing is deleted.'
                    : 'Everything fits under the Free limits. Confirming moves you to Free now; nothing is deleted.'}
                {freeCheck.items.filter((i) => !i.resolved).map((i) => (
                  <div key={i.limit} className="bp-impact-row">
                    <span className={`bp-dot ${i.blocking ? (i.action === 'delete_files' ? 'danger' : 'warn') : ''}`} />
                    {i.detail}
                  </div>
                ))}
                {/* Moving to Free is a plan change, and a plan change ends the
                    discount (Ben, 2026-09-26) — the plan picker's Free card says
                    the same. A pending credit is owed, so it is not named. */}
                {[
                  price.overridden ? 'Your agreed price ends when you make this change.' : '',
                  data.percentOff ? `Your ${data.percentOff.percent}% off ends when you make this change.` : '',
                ].filter(Boolean).map((line) => <div key={line} className="bp-impact-row">{line}</div>)}
                <button className="fs-btn sm neutral bp-impact-keep" onClick={() => setConfirmCancel(false)}>Keep my plan</button>
              </div>
            )}
          </RowCard>
        )}
        {cancelNote && <div className="bp-card-note" role="status">{cancelNote}</div>}
      </section>

      {/* ---- Invoices ---- */}
      <InvoicesCard invoices={invoices} billingConnected={billingConnected} onPortal={openPortal} />

      {/* ---- Storage, per form — the gate's delete_files affordance ---- */}
      <StorageCard />
      {guard.node}
    </div>
  );
}

/** The Card row: what Stripe says, and the one way to change it (the portal). */
function CardRow({ billingConnected, hasPaymentMethod, card, portalBusy, onPortal }: {
  billingConnected: boolean; hasPaymentMethod: boolean; card: CardSummary | null;
  portalBusy: boolean; onPortal: () => void;
}) {
  if (!billingConnected) {
    return (
      <RowCard label="Card">
        <b>Stripe isn&apos;t connected on this install.</b> No card exists and nothing has been
        charged through Form-Magic.
      </RowCard>
    );
  }
  // Driven by whether Stripe holds a payment method, never by whether a
  // Customer exists — every account has one of those now.
  return (
    <RowCard
      label="Card"
      hint="Entered in Stripe’s own portal, which links back here — never on this page."
      actions={(
        <button className="fs-btn text" disabled={portalBusy} onClick={onPortal}>
          {hasPaymentMethod ? 'Update card in Stripe' : 'Add a card in Stripe'}
        </button>
      )}
    >
      {hasPaymentMethod ? (
        card
          ? <span className="bp-card-line">{cardLine(card)}</span>
          : <span className="bp-card-line">Card on file ····</span>
      ) : (
        <>
          <b>No card yet.</b> Nothing has been charged. You can add one now, or at your first
          upgrade — either way it is entered inside Stripe, never here.
        </>
      )}
    </RowCard>
  );
}

/** "$100 per month after the trial · billed monthly · 100 forms, 5,000 submissions a month, 10 users, SSO included." */
function limitsDigest(limits: PlanLimits, sso: boolean): string {
  const fmt = (v: number | null) => (v === null ? INFINITY : v.toLocaleString());
  return `${fmt(limits.forms)} forms, ${fmt(limits.submissionsPerMonth)} submissions a month, `
    + `${fmt(limits.users)} user${limits.users === 1 ? '' : 's'}${sso ? ', SSO included.' : '.'}`;
}

function planRestated(
  cents: number | null, interval: 'month' | 'year', trialing: boolean,
  limits: PlanLimits, sso: boolean,
): string {
  const digest = limitsDigest(limits, sso);
  if (cents === null || cents === 0) return `Nothing to pay · ${digest}`;
  const per = interval === 'year' ? 'per year' : 'per month';
  const billed = interval === 'year' ? 'billed annually, up front' : 'billed monthly';
  return `${money(cents)} ${per}${trialing ? ' after the trial' : ''} · ${billed} · ${digest}`;
}

// ---------------------------------------------------------------------------
// This month — every meter against the entitled limits, as figure tiles.
// ---------------------------------------------------------------------------

type Tone = 'muted' | 'warn' | 'danger';

interface MeterSpec {
  key: string;
  label: string;
  /** null: a ceiling with no account-wide count (fields per form) */
  used: number | null;
  limit: number | null;
  storage?: boolean;
  sub: (left: number, atLimit: boolean) => string;
  /** the note for an unlimited ceiling (default "no limit on this plan") */
  unlimited?: string;
}

/** Amber from 80%, red at 100% — computed here, so a limit change needs no deploy. */
function meterState(m: MeterSpec): { ratio: number; tone: Tone } {
  if (m.used === null || m.limit === null) return { ratio: 0, tone: 'muted' };
  const ratio = m.limit === 0 ? 1 : Math.min(m.used / m.limit, 1);
  return { ratio, tone: ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : 'muted' };
}

function Meter({ m }: { m: MeterSpec }) {
  const show = (v: number) => (m.storage ? formatStorage(v) : v.toLocaleString());
  if (m.used === null) {
    // A per-form ceiling checked at publish: there is no account-wide count to
    // meter, so the tile states the ceiling rather than invent a number.
    return (
      <FigureTile label={m.label} value={m.limit === null ? INFINITY : m.limit.toLocaleString()}
        caption="per form" note={m.sub(0, false)} className="bp-tile" />
    );
  }
  if (m.limit === null) {
    return (
      <FigureTile label={m.label} value={show(m.used)} caption={m.unlimited || 'no limit on this plan'}
        className="bp-tile" />
    );
  }
  const { ratio, tone } = meterState(m);
  const left = Math.max(m.limit - m.used, 0);
  return (
    <FigureTile
      label={m.label} value={show(m.used)} caption={`of ${show(m.limit)}`}
      usage={{ used: m.used, limit: m.limit, near: 80 }}
      note={m.sub(left, ratio >= 1)} noteTone={tone} className="bp-tile"
    />
  );
}

const ALL_USAGE_KEY = 'fm.billing.allUsage';

function UsageSection({ usage, limits }: { usage: BillingView['usage']; limits: PlanLimits }) {
  const now = new Date();
  const day = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const period = `1–${day} ${MONTHS[now.getMonth()]} · resets 1 ${MONTHS[(now.getMonth() + 1) % 12]}`;
  // "on pace for about 2,660" — this month's run rate, rounded to tens.
  const pace = day > 0
    ? (Math.round((usage.submissions_this_month / day) * daysInMonth / 10) * 10).toLocaleString()
    : '0';
  const [showAll, setShowAll] = useState<boolean>(() => {
    try { return localStorage.getItem(ALL_USAGE_KEY) === 'open'; } catch { return false; }
  });
  const toggle = () => {
    setShowAll((v) => {
      try { localStorage.setItem(ALL_USAGE_KEY, v ? 'closed' : 'open'); } catch { /* a convenience only */ }
      return !v;
    });
  };

  // The design's four lead…
  const primary: MeterSpec[] = [
    { key: 'submissions', label: 'Submissions this month', used: usage.submissions_this_month, limit: limits.submissionsPerMonth,
      sub: (_l, at) => (at ? "at this month's cap — new responses see a closed form until it resets" : `on pace for about ${pace}`) },
    { key: 'forms', label: 'Forms', used: usage.forms, limit: limits.forms,
      sub: (left, at) => (at ? 'full — creating another needs a bigger plan' : `${left} left`) },
    { key: 'users', label: 'Users', used: usage.users, limit: limits.users,
      sub: (left, at) => (at ? 'full — inviting anyone else needs a bigger plan' : `${left} left`) },
    { key: 'storage', label: 'File storage', used: usage.storage_bytes, limit: limits.storageBytes, storage: true,
      sub: (_l, at) => (at ? 'full — uploads are refused until files are deleted' : `uploads across ${usage.forms.toLocaleString()} forms`) },
  ];
  // …and every other meter the plan has sits under "Show all usage".
  const rest: MeterSpec[] = [
    { key: 'records', label: 'Submission records kept', used: usage.submission_records, limit: limits.submissionRecords,
      sub: () => 'oldest records are pruned first, after an export warning' },
    { key: 'fields', label: 'Fields and widgets per form', used: null, limit: limits.fieldsPerForm,
      sub: () => 'checked per form when the builder saves' },
    // Only when the plan includes any (065/068): a 0/0 tile would meter a
    // feature the plan simply doesn't have.
    ...(limits.aiGenerationsPerMonth !== 0 ? [{
      key: 'ai', label: 'AI-generated forms this month', used: usage.ai_generations ?? 0, limit: limits.aiGenerationsPerMonth ?? null,
      sub: (left: number, at: boolean) => (at ? 'at this month’s cap — it resets on the 1st' : `${left} left · resets on the 1st`),
    }] : []),
    ...(limits.aiAssistantMessagesPerMonth !== 0 ? [{
      key: 'assistant', label: 'AI assistant questions this month', used: usage.ai_assistant_messages ?? 0,
      limit: limits.aiAssistantMessagesPerMonth ?? null,
      sub: (left: number, at: boolean) => (at ? 'at this month’s cap — it resets on the 1st' : `${left} left · resets on the 1st`),
    }] : []),
  ];
  // A meter at or near its ceiling is never folded away.
  const pressing = rest.filter((m) => meterState(m).tone !== 'muted');
  const shown = showAll ? [...primary, ...rest] : [...primary, ...pressing];
  const folded = rest.length - (showAll ? rest.length : pressing.length);

  return (
    <section className="bp-usage" aria-labelledby="bp-usage-title">
      <div className="bp-usage-head">
        <h2 id="bp-usage-title">This month</h2>
        <span className="bp-usage-period">{period}</span>
      </div>
      <p className="bp-usage-note">
        Anything at its ceiling is refused rather than billed as an overage — you&apos;ll never get a
        surprise invoice from us.
      </p>
      <div className="fs-tiles bp-tiles">
        {shown.map((m) => <Meter key={m.key} m={m} />)}
      </div>
      {(folded > 0 || showAll) && (
        <button type="button" className="fs-btn text bp-usage-more" aria-expanded={showAll} onClick={toggle}>
          {showAll ? 'Show less' : `Show all usage · ${folded} more`}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Invoices — the last-twelve mirror, upcoming row first, each with its PDF.
// ---------------------------------------------------------------------------

function invoicePill(inv: Invoice): { label: string; tone: string } {
  if (inv.number === 'upcoming' || inv.status === 'scheduled' || inv.status === 'upcoming') {
    return { label: 'scheduled', tone: 'info' };
  }
  if (inv.status === 'paid') {
    return (inv.attempt_count ?? 0) > 1
      ? { label: 'failed, then paid', tone: 'danger' }
      : { label: 'paid', tone: 'ok' };
  }
  if (inv.status === 'failed' || inv.status === 'uncollectible') {
    return { label: inv.status, tone: 'danger' };
  }
  return { label: inv.status, tone: 'neutral' };
}

/** PDF ↓ (streamed from Stripe through our server), and Receipt ↗ / Preview. */
function InvoiceLinks({ inv, billingConnected }: { inv: Invoice; billingConnected: boolean }) {
  const upcoming = inv.number === 'upcoming';
  const links: ReactNode[] = [];
  if (!upcoming && billingConnected) {
    links.push(
      <a key="pdf" className="fs-btn text sm" href={invoicePdfHref(inv.id)} download
        aria-label={`Download invoice ${inv.number || ''} as a PDF`.replace('  ', ' ')}>PDF ↓</a>,
    );
  }
  if (upcoming) {
    if (inv.hosted_url) links.push(<a key="p" className="fw-link" href={inv.hosted_url} target="_blank" rel="noreferrer">Preview</a>);
  } else if (inv.receipt_url || inv.hosted_url) {
    links.push(
      <a key="r" className="fw-link" href={(inv.receipt_url || inv.hosted_url)!} target="_blank" rel="noreferrer">Receipt ↗</a>,
    );
  }
  return <span className="bp-inv-links">{links}</span>;
}

function InvoicesCard({ invoices, billingConnected, onPortal }: {
  invoices: Invoice[]; billingConnected: boolean; onPortal: () => void;
}) {
  /** stacked rows instead of six columns (§4G G5) */
  const phone = useMediaQuery(PHONE);
  return (
    <TableCard
      title="Invoices"
      action={billingConnected
        ? <button className="fs-btn text" onClick={onPortal}>All invoices in Stripe</button>
        : undefined}
      footer={invoices.length > 0 ? (
        <span>
          The last twelve invoices are mirrored here from Stripe so this page works without leaving
          the app.{billingConnected
            ? <> Each one&apos;s PDF downloads from here; tax details and anything older live in the portal.</>
            : <> PDFs, tax details and anything older live in the portal.</>}
        </span>
      ) : undefined}
    >
      {invoices.length === 0 ? (
        <div className="st-card-body st-muted">
          {billingConnected
            ? 'Nothing invoiced yet. Your first invoice will appear here once a payment is taken.'
            : 'No invoices — payments are not switched on for this install, so you have not been charged through Form-Magic.'}
        </div>
      ) : phone ? (
        <div className="bp-inv-list">
          {invoices.map((inv) => {
            const pill = invoicePill(inv);
            return (
              <div className="bp-inv-row" key={inv.id}>
                <span className="when">
                  {shortDMY(inv.created_at)} · {money(inv.amount_cents, inv.currency)}
                </span>
                <span className="right">
                  <span className={`fs-pill ${pill.tone}`}>{pill.label}</span>
                  <InvoiceLinks inv={inv} billingConnected={billingConnected} />
                </span>
                <span className="what">{inv.number || '—'} · {inv.plan_name}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <table className="fs-table">
          <thead>
            <tr>
              <th>Date</th><th>Invoice</th><th>Plan</th>
              <th className="num">Amount</th><th>Status</th><th className="fs-row-actions"><span className="fs-sr-only">Files</span></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const pill = invoicePill(inv);
              return (
                <tr key={inv.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{shortDMY(inv.created_at)}</td>
                  <td><code className="st-mono">{inv.number || '—'}</code></td>
                  <td>{inv.plan_name}</td>
                  <td className="num">{money(inv.amount_cents, inv.currency)}</td>
                  <td><span className={`fs-pill ${pill.tone}`}>{pill.label}</span></td>
                  <td className="fs-row-actions"><InvoiceLinks inv={inv} billingConnected={billingConnected} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}

// ---------------------------------------------------------------------------
// File storage, per form — where "delete 6.1 GB" actually happens. The plan
// picker's storage gate row links straight here (#storage).
// ---------------------------------------------------------------------------

function StorageCard() {
  const [forms, setForms] = useState<StorageForm[] | null>(null);
  const [totalBytes, setTotalBytes] = useState(0);
  /**
   * The share of the total that belongs to the ACCOUNT and to no form — the
   * brand's logo (066). Shown as one extra row, and only when there is one.
   * It has no Delete of its own on purpose: these bytes go when the logo is
   * removed from Settings → Branding, where somebody can see what they are
   * about to un-brand thirty forms with.
   */
  const [accountBytes, setAccountBytes] = useState(0);
  const [storageErr, setStorageErr] = useState('');
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  /** one row per form instead of three columns (§4G G5) */
  const phone = useMediaQuery(PHONE);
  const ref = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    getStorage()
      .then((r) => { setForms(r.forms); setTotalBytes(r.totalBytes); setAccountBytes(r.accountBytes || 0); })
      .catch(() => setStorageErr("Per-form storage isn't available right now."));
  }, []);

  // The picker's "Review files" button lands on /admin/settings/billing#storage.
  useEffect(() => {
    if (location.hash === '#storage' && forms !== null) {
      ref.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [location.hash, forms]);

  async function onDelete(f: StorageForm) {
    if (confirmId !== f.id) { setConfirmId(f.id); return; }
    setConfirmId(null);
    setBusyId(f.id);
    try {
      const r = await deleteFormUploads(f.id);
      setTotalBytes(r.totalBytes);
      setForms((cur) => (cur || []).map((x) => (x.id === f.id ? { ...x, bytes: 0 } : x)));
    } catch (e) { setStorageErr((e as Error).message); }
    setBusyId(null);
  }

  const deleteLabel = (f: StorageForm) => (confirmId === f.id
    ? `Confirm — delete ${formatStorage(f.bytes)}; submission file links go dead`
    : "Delete this form's uploads");

  return (
    <div id="storage" ref={ref} className="bp-anchor">
      <TableCard
        title="File storage"
        hint={(
          <>
            Uploads, by the form that collected them. Bytes can&apos;t be made read-only, so moving to a
            smaller plan can mean deleting some — export first if you want a copy.
          </>
        )}
        action={forms !== null ? (
          <span className="bp-total">
            {/* "across N forms" stops being true once some of the total
                belongs to no form, so with a brand in it the line just says
                what the number is. */}
            {accountBytes > 0
              ? `${formatStorage(totalBytes)} in total`
              : `${formatStorage(totalBytes)} across ${forms.length} form${forms.length === 1 ? '' : 's'}`}
          </span>
        ) : undefined}
      >
        {storageErr && <div className="st-card-body st-muted">{storageErr}</div>}
        {forms !== null && forms.length === 0 && accountBytes === 0 && (
          <div className="st-card-body st-muted">No uploads yet — nothing is using storage.</div>
        )}
        {forms !== null && (forms.length > 0 || accountBytes > 0) && (phone ? (
          <div className="bp-store-list">
            {forms.map((f) => (
              <div className="bp-store-row" key={f.id}>
                <div className="head">
                  <span className="what">{f.name}</span>
                  {f.disabled && <span className="fs-pill neutral">disabled</span>}
                  <span className="size">{formatStorage(f.bytes)}</span>
                </div>
                {f.bytes > 0 && (
                  <button className="fs-btn sm danger go" disabled={busyId === f.id} onClick={() => onDelete(f)}>
                    <span className="say">{deleteLabel(f)}</span>
                  </button>
                )}
              </div>
            ))}
            {accountBytes > 0 && (
              <div className="bp-store-row">
                <div className="head">
                  <span className="what">Account branding</span>
                  <span className="size">{formatStorage(accountBytes)}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <table className="fs-table">
            <thead>
              <tr><th>Form</th><th className="num">Uploads</th><th className="fs-row-actions"><span className="fs-sr-only">Delete</span></th></tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id}>
                  <td>
                    {f.name}
                    {f.disabled && <span className="fs-pill neutral bp-inline-pill">disabled</span>}
                  </td>
                  <td className="num">{formatStorage(f.bytes)}</td>
                  <td className="fs-row-actions">
                    {f.bytes > 0 && (
                      <button className="fs-btn sm danger" disabled={busyId === f.id} onClick={() => onDelete(f)}>
                        {deleteLabel(f)}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {accountBytes > 0 && (
                <tr>
                  <td>Account branding</td>
                  <td className="num">{formatStorage(accountBytes)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        ))}
      </TableCard>
    </div>
  );
}
